import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
    Body1,
    Flex,
    Image,
    Stack,
    Subtitle1,
} from '@myphx-engineering/phx-design-system';

import {
    drugDescriptionMinWidth,
    drugImageMaxHeight,
    drugImageMaxWidth,
} from '../../constants';
import {
    useDrugProgram,
    useMedicationConfigOverride,
} from '../../contexts/DrugProgramProvider';
import { getFragment } from '../../graphql/client/helpers/get-fragment';
import { GetMedicationDocument } from '../../graphql/generated/graphql';
import { getDrugConfigByNdc } from '../../util/config/get-drug-config-by-medication';
import { formatMedicationQuantity } from '../../util/formatting/prescriber-order';
import { getProductDescriptionFromMedication } from '../../util/formatting/prescriber-order/get-product-description';
import { getAbsoluteRoute } from '../../util/get-absolute-route';
import { getNdcImage, getNdcImageOrientation } from '../../util/get-ndc-image';
import { Loader } from '../common/Loader';

export type MedicationFormInfo = {
    productDescription: string;
    quantity: number;
    formCode: string;
};

type SwitchPrescriptionHeaderProps = {
    switchedMedicationId: string;
    originalQuantity: number;
};

export const SwitchPrescriptionHeader = ({
    switchedMedicationId,
    originalQuantity,
}: SwitchPrescriptionHeaderProps) => {
    const { t, i18n } = useTranslation();
    const drugProgram = useDrugProgram();
    const configOverride = useMedicationConfigOverride();

    const { data, loading } = useQuery(GetMedicationDocument, {
        variables: { id: switchedMedicationId },
    });

    if (loading) {
        return <Loader />;
    }
    const medication = getFragment(data?.medication);

    const medicationOverride = configOverride.ndcInfo[switchedMedicationId];
    const quantity = formatMedicationQuantity(
        originalQuantity,
        medication,
        medicationOverride
    );
    const description = getProductDescriptionFromMedication(
        medication,
        i18n,
        medicationOverride
    );
    const drugConfig = getDrugConfigByNdc(drugProgram, switchedMedicationId);

    if (!drugConfig) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const image = getNdcImage(drugConfig, switchedMedicationId, configOverride);
    const imageOrientation = getNdcImageOrientation(
        switchedMedicationId,
        configOverride
    );

    const maxHeight =
        imageOrientation === 'landscape'
            ? drugImageMaxHeight
            : drugImageMaxWidth;
    const maxWidth =
        imageOrientation === 'landscape'
            ? drugImageMaxWidth
            : drugImageMaxHeight;

    return (
        <Stack>
            <Flex
                direction={imageOrientation === 'portrait' ? 'row' : 'column'}
                justify={
                    imageOrientation === 'portrait'
                        ? 'space-between'
                        : undefined
                }
                wrap={imageOrientation === 'portrait' ? 'wrap' : undefined}
                gap="xxs"
            >
                <Stack gap="xxs" miw={drugDescriptionMinWidth} flex={1}>
                    <Subtitle1>{description}</Subtitle1>

                    <Body1>
                        {t('order.quantity', {
                            quantity,
                        })}
                    </Body1>
                </Stack>

                <Image
                    src={image}
                    aria-hidden="true"
                    maw={maxWidth}
                    mah={maxHeight}
                    fit="contain"
                />
            </Flex>
        </Stack>
    );
};
